<template>
    <div class="otp-shell">
        <div class="otp-inner">

            <a href="/" class="otp-logo">
                <img :src="$platformBrand.logoUrl" :alt="$platformBrand.name" style="height:36px; width:auto;">
            </a>

            <h1 class="otp-heading">Check your phone</h1>
            <p class="otp-subtext">
                We sent a verification code to your number
                <span v-if="maskedPhone"> ending in <strong>...{{ maskedPhone }}</strong></span>.
            </p>

            <div v-if="successMessage" class="otp-alert otp-alert--success">
                {{ successMessage }}
            </div>

            <div v-if="genericError" class="otp-alert otp-alert--error">
                {{ genericError }}
            </div>

            <div class="form-group">
                <label class="otp-label" for="otp-code">Verification code</label>
                <input
                    id="otp-code"
                    v-model="form.otp"
                    type="text"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    autocomplete="one-time-code"
                    class="form-control otp-input"
                    placeholder="______"
                    maxlength="6"
                    ref="otpInput"
                    @input="onOtpInput"
                >
                <small v-if="errors.otp" class="text-danger">{{ errors.otp }}</small>
            </div>

            <button
                type="button"
                class="btn btn-primary btn-block otp-cta mt-4"
                :disabled="isLoading || form.otp.length < 6"
                @click="verifyOtp"
            >
                <span v-if="isLoading">
                    <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                    Verifying...
                </span>
                <span v-else>Verify <i class="bi bi-arrow-right"></i></span>
            </button>

            <p class="text-center mt-3 otp-resend">
                Didn't get the code?
                <span v-if="resendTimer > 0" class="text-muted">Resend in {{ resendTimer }}s</span>
                <a v-else href="#" class="text-dark font-weight-bold" @click.prevent="resendOtp">Resend code</a>
            </p>

        </div>
    </div>
</template>

<script>
export default {
    name: 'OtpVerification',

    data() {
        return {
            form: {
                phone: '',
                otp: '',
            },
            maskedPhone:    '',
            resendTimer:    60,
            timerInterval:  null,
            isLoading:      false,
            errors:         {},
            genericError:   '',
            successMessage: '',
        };
    },

    mounted() {
        const params = new URLSearchParams(window.location.search);
        const phone = params.get('otp-no');
        if (phone) {
            this.form.phone = phone;
            this.maskedPhone = phone.slice(-4);
        }
        this.startResendCountdown();
        this.$nextTick(() => this.$refs.otpInput && this.$refs.otpInput.focus());
    },

    beforeDestroy() {
        clearInterval(this.timerInterval);
    },

    methods: {
        onOtpInput() {
            this.form.otp = this.form.otp.replace(/\D/g, '').slice(0, 6);
            if (this.form.otp.length === 6) {
                this.verifyOtp();
            }
        },

        async verifyOtp() {
            if (this.form.otp.length < 6) return;
            this.errors = {};
            this.genericError = '';
            this.isLoading = true;
            try {
                const res = await axios.post('/api/verify-otp', this.form);
                if (res.status === 200) {
                    window.location.href = res.data.onboard_done ? '/app' : '/onboarding';
                }
            } catch (err) {
                if (err.response?.data?.errors) {
                    this.errors = err.response.data.errors;
                } else {
                    this.genericError = err.response?.data?.message
                        || 'Invalid code. Please try again.';
                }
            } finally {
                this.isLoading = false;
            }
        },

        async resendOtp() {
            this.genericError = '';
            this.successMessage = '';
            try {
                await axios.post('/api/resend-otp', { phone: this.form.phone });
                this.successMessage = 'A new code has been sent to your phone.';
                this.resendTimer = 30;
                this.startResendCountdown();
            } catch (_) {
                this.genericError = 'Could not resend the code. Please try again.';
            }
        },

        startResendCountdown() {
            clearInterval(this.timerInterval);
            this.timerInterval = setInterval(() => {
                if (this.resendTimer > 0) {
                    this.resendTimer--;
                } else {
                    clearInterval(this.timerInterval);
                }
            }, 1000);
        },
    },
};
</script>

<style scoped>
.otp-shell {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    padding: 2rem 1.25rem;
}

.otp-inner {
    width: 100%;
    max-width: 400px;
}

.otp-logo {
    display: block;
    text-align: center;
    margin-bottom: 2.5rem;
}

.otp-heading {
    font-size: 1.6rem;
    font-weight: 700;
    color: #212529;
    line-height: 1.25;
    margin-bottom: 0.5rem;
}

.otp-subtext {
    color: #6c757d;
    font-size: 0.9rem;
    margin-bottom: 2rem;
}

.otp-label {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6c757d;
    margin-bottom: 0.4rem;
    display: block;
}

.otp-input {
    border: none;
    border-bottom: 2px solid #dee2e6;
    border-radius: 0;
    padding-left: 0;
    background: transparent;
    font-size: 1.5rem;
    font-weight: 600;
    letter-spacing: 0.2em;
    color: #212529;
    box-shadow: none !important;
    transition: border-color 0.15s ease;
    text-align: center;
}

.otp-input:focus {
    border-bottom-color: #fd4719;
    background: transparent;
    outline: none;
}

.otp-cta {
    padding: 0.8rem 1rem;
    font-size: 1rem;
    font-weight: 600;
}

.otp-resend {
    font-size: 0.875rem;
}

.otp-alert {
    border-radius: 6px;
    padding: 0.65rem 1rem;
    font-size: 0.875rem;
    margin-bottom: 1.25rem;
}

.otp-alert--error {
    background: #fff5f5;
    border: 1px solid #f5c6cb;
    color: #842029;
}

.otp-alert--success {
    background: #f0fdf4;
    border: 1px solid #b7ebc8;
    color: #166534;
}
</style>
